import {
  Address,
  ComplianceEntity,
  ComplianceEntityForm,
  NullableComplianceEntityTaxClassification,
  ComplianceEntityType,
  Form,
  FormPartyTinType,
  FormStatus,
  FormW9FederalTaxClassification,
  Payee,
  VerificationLaneStatus
} from "@wingspanhq/wingspan-api-sdk";
import { getLast4 } from "../../../shared/utils/getLast4";
import { selectorComplianceEntityTaxLaneStatus } from "./selectorComplianceEntityTaxLaneStatus";

/**
 * The contractor W-9 fields the payer-facing `ContractorInformation` widget
 * renders, resolved from v3. Dual-source: a **payer-supplied** contractor's W-9
 * lives on the payer-owned `ComplianceEntity`; a **payee-owned** (signed-up)
 * contractor's W-9 is only payer-readable via the projected Auto-W-9 `Form`.
 * Prefers the CE; falls back to the Form. Verdict is granular from the CE Tax
 * lane, coarse (Effective→Verified, else Pending) from the Form. Read-only.
 */
export type ContractorTaxDisplay = {
  tinType: "Individual" | "Business";
  legalName?: string;
  disregardedEntityName?: string;
  country: string;
  taxClassificationLabel?: string;
  maskedTaxId?: string;
  address?: string;
  verificationStatus?: "Verified" | "Failed" | "Pending";
};

const formatAddress = (address?: Address | null): string | undefined =>
  address
    ? [
        address.line1,
        address.line2,
        [address.city, address.state, address.postalCode]
          .filter(Boolean)
          .join(" ")
      ]
        .filter(Boolean)
        .join("\n")
        .trim() || undefined
    : undefined;

const complianceEntityClassificationLabel = (
  complianceEntity: ComplianceEntity
): string | undefined => {
  if (complianceEntity.type === ComplianceEntityType.INDIVIDUAL) {
    return "Sole proprietorship / Single member LLC";
  }
  switch (complianceEntity.taxClassification) {
    case NullableComplianceEntityTaxClassification.TAXED_AS_S_CORPORATION:
      return "S corporation";
    case NullableComplianceEntityTaxClassification.TAXED_AS_C_CORPORATION:
      return "C corporation";
    case NullableComplianceEntityTaxClassification.TAXED_AS_PARTNERSHIP:
      return "Partnership";
    case NullableComplianceEntityTaxClassification.TAXED_AS_DISREGARDED_ENTITY:
      return "Single member LLC";
    default:
      return complianceEntity.form === ComplianceEntityForm.NON_PROFIT
        ? "Non-profit"
        : undefined;
  }
};

const formClassificationLabel = (
  classification?: FormW9FederalTaxClassification
): string | undefined => {
  switch (classification) {
    case FormW9FederalTaxClassification.INDIVIDUAL:
      return "Sole proprietorship / Single member LLC";
    case FormW9FederalTaxClassification.C_CORPORATION:
      return "C corporation";
    case FormW9FederalTaxClassification.S_CORPORATION:
      return "S corporation";
    case FormW9FederalTaxClassification.PARTNERSHIP:
      return "Partnership";
    case FormW9FederalTaxClassification.TRUST_ESTATE:
      return "Trust/estate";
    case FormW9FederalTaxClassification.LLC:
      return "Limited liability company";
    default:
      return classification ? "Other" : undefined;
  }
};

export const selectorContractorTaxDisplay = (params: {
  payee?: Payee;
  complianceEntity?: ComplianceEntity;
  form?: Form;
}): ContractorTaxDisplay | undefined => {
  const { payee, complianceEntity, form } = params;

  if (complianceEntity) {
    const isBusiness = complianceEntity.type === ComplianceEntityType.BUSINESS;
    const laneStatus = selectorComplianceEntityTaxLaneStatus(complianceEntity);
    const taxIdentifier = complianceEntity.taxIdentifiers?.[0];

    return {
      tinType: isBusiness ? "Business" : "Individual",
      legalName:
        complianceEntity.legalName ||
        [
          complianceEntity.individualLegalName?.givenName,
          complianceEntity.individualLegalName?.familyName
        ]
          .filter(Boolean)
          .join(" ") ||
        payee?.profile?.displayName ||
        undefined,
      disregardedEntityName: isBusiness
        ? complianceEntity.doingBusinessAs || undefined
        : undefined,
      country: complianceEntity.physicalAddress?.country || "US",
      taxClassificationLabel:
        complianceEntityClassificationLabel(complianceEntity),
      maskedTaxId: taxIdentifier?.value
        ? "•••••" + getLast4(taxIdentifier.value)
        : taxIdentifier?.hasValue
          ? "•••••"
          : undefined,
      address: formatAddress(complianceEntity.physicalAddress),
      verificationStatus:
        laneStatus === VerificationLaneStatus.VERIFIED
          ? "Verified"
          : laneStatus === VerificationLaneStatus.FAILED
            ? "Failed"
            : laneStatus
              ? "Pending"
              : undefined
    };
  }

  if (form) {
    const isBusiness = form.recipient?.tinType === FormPartyTinType.EIN;
    // The list is filtered to W-9 forms, so `formData` is the W-9 variant; the
    // discriminated union double-declares `type`, so read the field via a
    // minimal cast rather than fighting the narrowing.
    const w9FederalClassification = (
      form.formData as
        | { federalTaxClassification?: FormW9FederalTaxClassification }
        | undefined
    )?.federalTaxClassification;

    return {
      tinType: isBusiness ? "Business" : "Individual",
      legalName:
        form.recipient?.name || payee?.profile?.displayName || undefined,
      country: form.recipient?.address?.country || "US",
      taxClassificationLabel: formClassificationLabel(w9FederalClassification),
      maskedTaxId: form.recipient?.tin || undefined,
      address: formatAddress(form.recipient?.address),
      verificationStatus:
        form.status === FormStatus.EFFECTIVE ? "Verified" : "Pending"
    };
  }

  return undefined;
};
